/**
 * F39 (Wave 2C) — admin override-cancel placement modal (D4).
 *
 * Mandatory reason (10–500) + optional goodwill credit-grant (USD minor units)
 * → POST /admin/placements/:id/cancel. The server transitions → cancelled +
 * refunds/grants credit as configured, and audit-logs the reason. Gated
 * server-side on `placement_approvals:approve`.
 */
import { useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  CurrencyInput,
  FormField,
  Modal,
  Switch,
  Textarea,
} from '@bravemove/ui-web';
import { CancelPlacementInputSchema } from '@bravemove/types';

export interface CancelPlacementDialogProps {
  readonly open: boolean;
  readonly currency: string;
  readonly onConfirm: (reason: string, grantCreditMinor?: number) => Promise<void>;
  readonly onClose: () => void;
}

export function CancelPlacementDialog({
  open,
  currency,
  onConfirm,
  onClose,
}: CancelPlacementDialogProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [grantCredit, setGrantCredit] = useState(false);
  const [creditMinor, setCreditMinor] = useState(0);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    /* eslint-disable react-hooks/set-state-in-effect */
    setReason('');
    setGrantCredit(false);
    setCreditMinor(0);
    setServerError(null);
    setSubmitting(false);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [open]);

  const reasonTrimmed = reason.trim();
  const candidate = {
    reason: reasonTrimmed,
    ...(grantCredit ? { grantCreditMinor: creditMinor } : {}),
  };
  const valid = CancelPlacementInputSchema.safeParse(candidate).success;

  const submit = async () => {
    setServerError(null);
    if (!valid) {
      setServerError(
        t('placement:admin.monitor.cancel_invalid', {
          defaultValue: 'Provide a reason (10–500 characters).',
        }),
      );
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(reasonTrimmed, grantCredit ? creditMinor : undefined);
      onClose();
    } catch {
      setServerError(
        t('placement:admin.inventory.error_generic', {
          defaultValue: 'Something went wrong. Please try again.',
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('placement:admin.monitor.override_cancel', { defaultValue: 'Cancel placement' })}
      description={t('placement:admin.monitor.cancel_description', {
        defaultValue:
          'Admin override-cancel. Optionally grant goodwill credit for the affected venue.',
      })}
      size="sm"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="cancel-placement-cancel"
          >
            {t('common:cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void submit()}
            loading={submitting}
            disabled={submitting || !valid}
            data-testid="cancel-placement-confirm"
          >
            {t('placement:admin.monitor.override_cancel', { defaultValue: 'Cancel placement' })}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-3"
        data-testid="cancel-placement-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <FormField
          label={t('placement:admin.monitor.cancel_reason', {
            defaultValue: 'Reason for cancellation',
          })}
          required
        >
          <Textarea
            rows={3}
            maxLength={500}
            value={reason}
            data-testid="cancel-placement-reason"
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>

        <FormField
          label={t('placement:admin.monitor.grant_credit', { defaultValue: 'Grant credit' })}
        >
          <Switch
            checked={grantCredit}
            data-testid="cancel-placement-grant-toggle"
            onChange={(e) => setGrantCredit(e.target.checked)}
            label={t('placement:admin.monitor.grant_credit_toggle', {
              defaultValue: 'Grant goodwill credit',
            })}
          />
        </FormField>

        {grantCredit ? (
          <FormField
            label={t('placement:admin.monitor.grant_credit_amount', {
              defaultValue: 'Credit amount',
            })}
          >
            <CurrencyInput
              value={creditMinor}
              currency={currency}
              ariaLabel={t('placement:admin.monitor.grant_credit_amount', {
                defaultValue: 'Credit amount',
              })}
              onChange={(v) => setCreditMinor(v)}
            />
          </FormField>
        ) : null}

        {serverError ? (
          <div data-testid="cancel-placement-server-error">
            <Banner tone="danger">{serverError}</Banner>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

export default CancelPlacementDialog;
